import { useState } from "react";
import { streamPost } from "../lib/api";
import { CopyBtn } from "../components/CopyBtn";
import { Loader2, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { track } from "../lib/analytics";
import { ImageAttach } from "../components/ImageAttach";

const FORMATS = [
  { id: "twitter_thread", label: "twitter thread" },
  { id: "tiktok_script", label: "tiktok script" },
  { id: "instagram_reel", label: "instagram reel" },
  { id: "billboard", label: "billboard" },
  { id: "radio_spot", label: "radio spot" },
  { id: "newsletter", label: "newsletter" },
];

export const AdLab = () => {
  const [concept, setConcept] = useState(
    "An AI tries writing a prompt for a human — comically over-formal, XML tags everywhere. The human replies with 'uhh what?' Then a human takes over and writes a beautiful, clear prompt for the AI. Show both worlds side by side."
  );
  const [format, setFormat] = useState("twitter_thread");
  const [count, setCount] = useState(3);
  const [out, setOut] = useState("");
  const [busy, setBusy] = useState(false);
  const [image, setImage] = useState(null);

  const run = async () => {
    if (!concept.trim()) { toast.error("give the ad lab a concept first"); return; }
    setOut(""); setBusy(true);
    track("adlab_submitted", { format, count: Number(count), has_image: !!image });
    try {
      const body = { concept, format, count: Number(count) };
      if (image) { body.image_base64 = image.base64; body.image_mime = image.mime; }
      await streamPost("/adlab", body,
        (_c, full) => setOut(full),
        () => { setBusy(false); track("adlab_completed", { format, count: Number(count), has_image: !!image }); }
      );
    } catch (e) {
      toast.error("ad lab failed: " + e.message);
      track("adlab_failed", { error: e.message });
      setBusy(false);
    }
  };

  return (
    <div data-testid="adlab-page" className="mx-auto max-w-6xl px-6 py-16">
      <div className="mb-10">
        <div className="text-[10px] uppercase tracking-[0.3em] text-[color:var(--accent-matrix)] mb-3">// module.ad_lab</div>
        <h1 className="font-display font-bold text-4xl sm:text-5xl tracking-tight">
          the ad lab<span className="cursor" />
        </h1>
        <p className="text-neutral-400 mt-4 max-w-2xl">
          Generate funny, original promo scripts for sAimonSays.Ai. Copy them to post manually, or pipe them into your
          scheduler of choice (Buffer, Hootsuite, Publer, Zapier, Make, or the X API) to automate the whole rhythm.
        </p>
      </div>

      <div className="border border-white/10">
        <div className="p-6 border-b border-white/10 bg-[#0a0a0a]">
          <label className="text-[10px] uppercase tracking-[0.3em] text-neutral-500">// concept</label>
          <textarea
            data-testid="adlab-concept-input"
            value={concept}
            onChange={(e) => setConcept(e.target.value)}
            rows={4}
            className="mt-2 w-full bg-black border border-white/10 focus:border-[color:var(--accent-amber)] outline-none p-4 text-sm resize-none"
            style={{ transitionProperty: "border-color", transitionDuration: "150ms" }}
          />

          <div className="mt-4 grid grid-cols-1 md:grid-cols-[1fr_auto] gap-4 items-end">
            <div>
              <label className="text-[10px] uppercase tracking-[0.3em] text-neutral-500">// primary format</label>
              <div className="mt-2 flex flex-wrap gap-1">
                {FORMATS.map((f) => (
                  <button
                    key={f.id}
                    data-testid={`adlab-format-${f.id}`}
                    onClick={() => setFormat(f.id)}
                    className={`px-3 py-2 text-[10px] uppercase tracking-widest border duration-150 ${
                      format === f.id
                        ? "border-[color:var(--accent-amber)] text-[color:var(--accent-amber)]"
                        : "border-white/10 text-neutral-400 hover:border-white/40"
                    }`}
                    style={{ transitionProperty: "color, border-color" }}
                  >
                    {f.label}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-[0.3em] text-neutral-500">// count</label>
              <select
                data-testid="adlab-count-select"
                value={count}
                onChange={(e) => setCount(e.target.value)}
                className="mt-2 w-full bg-black border border-white/10 focus:border-[color:var(--accent-amber)] outline-none px-3 py-2 text-sm"
              >
                {[1, 2, 3, 4, 5].map((n) => (<option key={n} value={n}>{n} ads</option>))}
              </select>
            </div>
          </div>

          <div className="mt-6">
            <ImageAttach image={image} onChange={setImage} label="// reference image (optional)" testIdPrefix="adlab" />
          </div>

          <button
            data-testid="adlab-run-btn"
            onClick={run}
            disabled={busy}
            className="mt-6 inline-flex items-center gap-2 bg-[color:var(--accent-amber)] text-black px-6 py-3 text-xs uppercase tracking-widest font-bold hover:bg-white disabled:opacity-50 duration-150"
            style={{ transitionProperty: "background-color, opacity" }}
          >
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
            {busy ? "cooking..." : "brew some ads"}
          </button>
        </div>

        <div className="p-6 bg-[#050505] min-h-[400px]">
          <div className="flex items-center justify-between mb-3">
            <label className="text-[10px] uppercase tracking-[0.3em] text-neutral-500">// output</label>
            {out && !busy && <CopyBtn text={out} testId="adlab-copy" label="copy all" />}
          </div>
          {!out && !busy && (
            <div className="text-neutral-600 text-sm border border-dashed border-white/10 p-12 text-center">
              ad scripts will stream here<span className="cursor" />
            </div>
          )}
          {(out || busy) && (
            <pre data-testid="adlab-output" className="whitespace-pre-wrap text-xs leading-relaxed text-neutral-200 overflow-auto">
{out}{busy && <span className="cursor" />}
            </pre>
          )}
        </div>
      </div>
    </div>
  );
};
